import { defineComponent, computed, onMounted, ref, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { usePermissionsStore } from '@/store/permissions.store';
import { useCashierSettingsStore } from '@/store/cashier-settings/cashier-settings.store';
import { Setting } from '@element-plus/icons-vue';
import {
  type CashierConfig,
  cashierConfigListColumns,
  CashierSettingsColumnProperties,
} from '@/models/cashier-settings/cashier-settings.model';
import type { ColumnFilterItem } from '@/models/column-filter/column-filter.model';
import { CASHIER_SETTINGS_PERMISSIONS } from '@/models/app-internal/permissions.model';
import CurrencyTable from '@/layouts/cashier-settings-page/currency-table/currency-table.vue';
import type { Sort, TableInstance } from 'element-plus';
import { SortingMapping, getSortValue } from '@marfa/bo-utils';
import { SortParams } from '@/models/app-common.model';

const icons = {
  Setting,
};

export default defineComponent({
  name: 'ConfigListTable',
  components: {
    CurrencyTable,
  },
  setup() {
    const { cashierSettingsPermissions } = usePermissionsStore();

    const cashierSettingsStore = useCashierSettingsStore();
    const {
      getCurrencyListFromConfig,
      getCashierConfigList,
      setSelectedCountry,
      setCashierSort,
      resetCashierSort,
    } = cashierSettingsStore;
    const { cashierConfigList, isCashierConfigListLoading } = storeToRefs(cashierSettingsStore);

    const defaultCashierSortState = ref<Sort>({
      prop: CashierSettingsColumnProperties.COUNTRY_NAME,
      order: SortParams.ASCENDING,
    });
    const tableRef = ref<TableInstance>();

    const isChangeCountrySettingsAvailable = computed<boolean>(() =>
      cashierSettingsPermissions[CASHIER_SETTINGS_PERMISSIONS.CREATE_CASHIER_CONFIG] &&
      cashierSettingsPermissions[CASHIER_SETTINGS_PERMISSIONS.UPDATE_CASHIER_CONFIG]);

    const getRowClassName: ({ row }: Record<'row', CashierConfig>) => string =
      ({ row }) => !getCurrencyListFromConfig(row).length ? 'no-expand' : '';

    const getColumnType = (column: ColumnFilterItem): string => {
      return column.value === CashierSettingsColumnProperties.EXPAND ? 'expand' : '';
    };

    const getCurrencyTableVisibility: (row: CashierConfig, column: ColumnFilterItem) => boolean =
      (row, column) => {
        const currencyListFromConfig = cashierSettingsStore.getCurrencyListFromConfig(row);

        return column.value === CashierSettingsColumnProperties.EXPAND && Boolean(currencyListFromConfig.length);
      };

    const changeColumnSort = ({ prop, order }: { prop: string; order: string }): void => {
      setCashierSort(getSortValue({ columnName: prop, order: SortingMapping[order] }));

      getCashierConfigList();
    };

    onMounted(() => {
      getCashierConfigList();
    });

    onUnmounted(() => {
      resetCashierSort();
    });

    return {
      icons,
      getColumnType,
      getRowClassName,
      cashierConfigListColumns,
      getCurrencyTableVisibility,
      CashierSettingsColumnProperties,
      isChangeCountrySettingsAvailable,
      setSelectedCountry,
      getCurrencyListFromConfig,
      cashierConfigList,
      isCashierConfigListLoading,
      defaultCashierSortState,
      changeColumnSort,
      tableRef,
    };
  },
});
